import type { EChartsOption } from "echarts";
import ReactECharts from "echarts-for-react";
import type { RefObject } from "react";
import type {
	InspectionDashboardDefectTrendPoint,
	InspectionDashboardGrain,
} from "../../../models/InspectionDashboard";
import { formatCount, formatPercent, rate } from "../../utils/dashboardFormat";
import { DASHBOARD_INK } from "../../utils/dashboardTokens";
import { getInspectionDashboardBucketLabel } from "../../utils/period";

type TooltipEntry = {
	seriesName?: unknown;
	value?: unknown;
	color?: unknown;
	dataIndex?: unknown;
	data?: unknown;
	axisValueLabel?: unknown;
};

type Props = {
	trend: InspectionDashboardDefectTrendPoint[];
	grain: InspectionDashboardGrain;
	isEvent: boolean;
	height: number;
	chartRef?: RefObject<ReactECharts>;
	onEvents?: Record<string, (params: unknown) => void>;
};

export const DefectTrendChart = ({
	trend,
	grain,
	isEvent,
	height,
	chartRef,
	onEvents,
}: Props) => {
	const axis = trend.filter((point) => point.TopRank === 1);
	const labels = axis.map((point) =>
		getInspectionDashboardBucketLabel(point, grain, isEvent),
	);

	const ranks = [...new Set(trend.map((point) => point.TopRank))].sort(
		(a, b) => a - b,
	);

	const series = ranks.map((rank) => {
		const points = trend
			.filter((point) => point.TopRank === rank)
			.sort((a, b) => a.BucketOrdinal - b.BucketOrdinal);

		return {
			name: points[0]?.SurfaceDefectName ?? "",
			color: points[0]?.Color || DASHBOARD_INK.AXIS,
			data: points.map((point): { value: number; count: number } => ({
				value: rate(point.DefectCount, point.BucketDefects) ?? 0,
				count: point.DefectCount,
			})),
		};
	});

	const renderTooltip = (parameters: unknown): string => {
		const entries = Array.isArray(parameters)
			? (parameters as TooltipEntry[])
			: [];
		if (entries.length === 0) return "";

		const header =
			typeof entries[0].axisValueLabel === "string"
				? entries[0].axisValueLabel
				: "";

		const dataIndex =
			typeof entries[0].dataIndex === "number" ? entries[0].dataIndex : -1;
		const bucketDefects = axis[dataIndex]?.BucketDefects ?? 0;

		if (bucketDefects === 0)
			return `<div style="min-width:170px">
				<div style="font-weight:600">${header}</div>
				<div style="color:${DASHBOARD_INK.MUTED};margin-top:4px">No defects tagged</div>
			</div>`;

		const countOf = (entry: TooltipEntry) =>
			(entry.data as { value: number; count: number } | undefined)?.count ?? 0;

		const topCount = entries.reduce((sum, entry) => sum + countOf(entry), 0);

		const rows = entries
			.map((entry) => {
				const color =
					typeof entry.color === "string" ? entry.color : "transparent";
				const count = countOf(entry);

				return `<div style="display:flex;align-items:center;gap:6px;margin-top:4px">
					<span style="width:8px;height:8px;border-radius:50%;background:${color}"></span>
					<span style="color:${DASHBOARD_INK.SECONDARY}">${entry.seriesName}</span>
					<span style="margin-left:auto;padding-left:12px;font-variant-numeric:tabular-nums"><b>${formatPercent(rate(count, bucketDefects), 0)}</b> · ${formatCount(count)}</span>
				</div>`;
			})
			.join("");

		return `<div style="min-width:190px">
			<div style="font-weight:600">${header}</div>
			${rows}
			<div style="display:flex;margin-top:6px;padding-top:4px;border-top:1px solid ${DASHBOARD_INK.GRID}">
				<span style="color:${DASHBOARD_INK.MUTED}">Top ${entries.length} of ${formatCount(bucketDefects)} tagged</span>
				<span style="margin-left:auto;padding-left:12px;font-variant-numeric:tabular-nums"><b>${formatPercent(rate(topCount, bucketDefects), 0)}</b> · ${formatCount(topCount)}</span>
			</div>
		</div>`;
	};

	const option: EChartsOption = {
		grid: { top: 10, right: 12, bottom: 4, left: 4, containLabel: true },
		tooltip: {
			trigger: "axis",
			appendTo: "body",
			axisPointer: { type: "line", lineStyle: { color: DASHBOARD_INK.AXIS } },
			borderColor: DASHBOARD_INK.GRID,
			formatter: renderTooltip,
		},
		xAxis: {
			type: "category",
			data: labels,
			boundaryGap: false,
			axisLine: { lineStyle: { color: DASHBOARD_INK.AXIS } },
			axisTick: { show: false },
			axisLabel: {
				color: DASHBOARD_INK.MUTED,
				fontSize: 10,
				hideOverlap: true,
			},
		},
		yAxis: {
			type: "value",
			min: 0,
			max: 100,
			interval: 25,
			axisLabel: {
				color: DASHBOARD_INK.MUTED,
				fontSize: 10,
				formatter: "{value}%",
			},
			splitLine: { lineStyle: { color: DASHBOARD_INK.GRID } },
		},
		series: series.map((line) => ({
			name: line.name,
			type: "line" as const,
			data: line.data,
			color: line.color,
			lineStyle: { width: 1.75 },
			symbol: "emptyCircle",
			symbolSize: 5,
			showSymbol: labels.length <= 12,
			triggerLineEvent: true,
			emphasis: { focus: "series" as const },
		})),
	};

	return (
		<ReactECharts
			ref={chartRef}
			option={option}
			notMerge
			onEvents={onEvents}
			style={{ height, width: "100%" }}
			opts={{ renderer: "svg" }}
		/>
	);
};
